import { memo, useMemo, useEffect, useRef } from 'react';
import { Handle, Position, NodeProps, useUpdateNodeInternals } from "reactflow";
import { NodeResizer } from "@reactflow/node-resizer";
import "@reactflow/node-resizer/dist/style.css";
import {
  Server,
  ShieldCheck,
  Bot,
  Globe,
  History,
  FileJson,
  Wand2,
  HardDrive,
  Shield,
  Lock,
  Settings,
  Users,
} from "lucide-react";
import mrClusterLogo from "/assets/mr_cluster.svg";
import { ClusterNodeData, HandleConfig } from "../../types";
import { useUIContext } from '../../providers/UIProvider';
import {
  Environment,
  ENVIRONMENTS,
  MODULES,
  ModuleConfig,
} from "../../types/cluster";
import { useNodeResizing } from '../../hooks/useNodeResizing';
import { NODE_DIMENSIONS } from '../../utils/nodeHelpers';

// Create a mapping for Lucide React icons
const lucideIconMap: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  cloud: HardDrive,
  shield: Shield,
  aws: Server,
  gcp: Server,
  azure: Server,
  lock: Lock,
  custom: Settings,
  "shield-check": ShieldCheck,
  robot: Bot,
  globe: Globe,
  refresh: History,
  "check-square": FileJson,
  transform: Wand2,
};

// Mapping for shortened module names
const SHORT_MODULE_NAMES: Record<string, string> = {
  'consent-enforcement': 'Consent',
  'bot-detection': 'Bots',
  'data-replay': 'Replay',
  'schema-enforcement': 'Schema',
  'transformations': 'Transform'
};

// Dynamically generate MODULE_ICONS from MODULES
const MODULE_ICONS = MODULES.reduce((acc, module) => {
  if (module.icon && module.name) {
    const IconComponent = lucideIconMap[module.icon];
    if (IconComponent) {
      acc[module.id] = { 
        icon: IconComponent, 
        label: SHORT_MODULE_NAMES[module.id] || module.name 
      };
    }
  }
  return acc;
}, {} as Record<
  string,
  { icon: React.ComponentType<{ className?: string }>; label: string }
>);

// Helper function to calculate dynamic style for handles on a side
const calculateHandleStyle = (index: number, total: number, handlePosition: Position): React.CSSProperties => {
  const calcOffset = 5;
  const percentage = (index + 1) * (100 / (total + 1));

  switch (handlePosition) {
    case Position.Top:
      return { left: `calc(${percentage}% - ${calcOffset}px)`, top: `-${calcOffset}px` };
    case Position.Bottom:
      return { left: `calc(${percentage}% - ${calcOffset}px)`, bottom: `-${calcOffset}px` };
    case Position.Left:
    case Position.Right:
    default:
      return { top: `calc(${percentage}% - ${calcOffset}px)` };
  }
};

const ClusterNode = ({ data, selected, id }: NodeProps<ClusterNodeData>) => {
  const { } = useUIContext();
  const updateNodeInternals = useUpdateNodeInternals();
  const nodeContentRef = useRef<HTMLDivElement>(null);

  // Calculate minimum height based on content
  const calculateMinHeight = useMemo(() => {
    return (width: number, headerHeight: number) => {
      let calculatedHeight = NODE_DIMENSIONS.VERTICAL_PADDING;
      calculatedHeight += headerHeight || NODE_DIMENSIONS.HEADER_CONTENT_ESTIMATED_HEIGHT;

      const showLabels = width >= NODE_DIMENSIONS.HIDE_LABELS_WIDTH_THRESHOLD;
      if (showLabels) {
        let visibleLabelCount = 0;
        if (data.clusterEnvironment && data.environmentVisibility !== "hidden") visibleLabelCount++;
        if (data.teamAccess && data.teamAccessVisibility !== "hidden") visibleLabelCount++;
        if (data.customLabel && data.customLabelVisibility !== "hidden") visibleLabelCount++;

        if (visibleLabelCount > 0) {
          calculatedHeight += NODE_DIMENSIONS.LABEL_ROW_ESTIMATED_HEIGHT;
        }
        if (visibleLabelCount > 2 && width < (NODE_DIMENSIONS.MIN_WIDTH + 60)) {
          calculatedHeight += NODE_DIMENSIONS.LABEL_ROW_ESTIMATED_HEIGHT;
        }
      }

      const enabledModules = Array.isArray(data.modules) ? data.modules.filter((module: ModuleConfig) => module.enabled) : [];
      if (enabledModules.length > 0 && data.moduleVisibility !== "hidden") {
        calculatedHeight += NODE_DIMENSIONS.MODULES_MIN_VISIBLE_HEIGHT_ADDITION;
      }
      
      return Math.min(calculatedHeight, NODE_DIMENSIONS.MAX_HEIGHT);
    };
  }, [data]);

  // Use the node resizing hook
  const { 
    onResize, 
    onResizeEnd, 
    currentWidth, 
    currentHeight,
    effectiveMinHeight 
  } = useNodeResizing({
    id,
    nodeType: 'cluster', // Pass the node type
    minWidth: NODE_DIMENSIONS.MIN_WIDTH,
    maxWidth: NODE_DIMENSIONS.MAX_WIDTH,
    minHeight: NODE_DIMENSIONS.MIN_HEIGHT,
    maxHeight: NODE_DIMENSIONS.MAX_HEIGHT,
    contentRef: nodeContentRef,
    calculateMinHeight
  });

  // Effect to update node internals when handles change
  useEffect(() => {
    if (Array.isArray(data.handles)) {
      updateNodeInternals(id);
    }
  }, [id, data.handles, updateNodeInternals]);

  // --- Handle Rendering Logic (copied and adapted from DestinationNode) ---
  const handles = useMemo(() => {
    const getDefaultClusterHandles = (nodeId: string): HandleConfig[] => [
      { id: `cluster-${nodeId}-ingestor`, type: 'target', position: Position.Left, color: '#3B82F6' },
      { id: `cluster-${nodeId}-forwarders`, type: 'source', position: Position.Right, color: '#10B981' },
    ];
    return Array.isArray(data.handles) ? [...data.handles] : getDefaultClusterHandles(id);
  }, [data.handles, id]);

  const handlesByPosition = useMemo(() => {
    return handles.reduce((acc, handle) => {
      if (!acc[handle.position]) {
        acc[handle.position] = [];
      }
      acc[handle.position].push(handle);
      return acc;
    }, {} as Record<Position, HandleConfig[]>);
  }, [handles]);
  // --- End Handle Rendering Logic ---

  // Get environment color based on environment category
  const getEnvironmentColor = (environmentId: string): string => {
    const environment: Environment | undefined = ENVIRONMENTS.find((env) => env.id === environmentId);
    if (!environment) {
      return 'bg-gray-100 text-gray-800';
    }
    switch (environment.category) {
      case 'managed':
        return 'bg-indigo-100 text-indigo-800';
      case 'private':
        return 'bg-green-100 text-green-800';
      case 'cloud':
        return 'bg-sky-100 text-sky-800';
      case 'special':
        return 'bg-pink-100 text-pink-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to get the environment icon path based on environment category
  const getEnvironmentIconPath = (environmentId: string): string => {
    const environment: Environment | undefined = ENVIRONMENTS.find((env) => env.id === environmentId);
    if (!environment) {
      return '';
    }
    switch (environment.category) {
      case 'managed':
        return '/assets/cloudenv_gcp.svg';
      case 'private':
        return '/assets/cloudenv_secure.svg';
      case 'cloud':
        return environment.id.includes('aws') ? '/assets/cloudenv_aws.svg' : 
               environment.id.includes('azure') ? '/assets/cloudenv_azure.svg' : 
               '/assets/cloudenv_gcp.svg';
      case 'special':
        return '/assets/cloudenv_closed.svg';
      default:
        return '';
    }
  };

  return (
    <div
      style={{
        width: currentWidth,
        height: currentHeight,
        position: 'relative',
      }}
      className="node-wrapper"
    >
      {selected && (
        <NodeResizer
          minWidth={NODE_DIMENSIONS.MIN_WIDTH}
          maxWidth={NODE_DIMENSIONS.MAX_WIDTH}
          minHeight={effectiveMinHeight}
          maxHeight={NODE_DIMENSIONS.MAX_HEIGHT}
          isVisible={selected}
          onResize={onResize}
          onResizeEnd={onResizeEnd}
          handleStyle={{
            width: '8px',
            height: '8px',
            backgroundColor: '#3b82f6', // blue-500
            border: '1px solid white',
            borderRadius: '2px',
          }}
          lineStyle={{
            borderColor: '#3b82f6',
            borderWidth: '1px',
          }}
        />
      )}
      
      <div
        className={`
          w-full h-full pl-4 pr-4 pb-3 pt-2.5 rounded-lg bg-white dark:bg-gray-700 border transition-all shadow-sm dark:shadow-sm-dark hover:shadow-md dark:hover:shadow-md-dark overflow-hidden
          flex flex-col justify-center items-center
          ${selected 
            ? "border-indigo-500 shadow-indigo-100 outline outline-4 outline-indigo-500/20 outline-offset-0" 
            : "border-gray-300 dark:border-gray-600"
          }
        `}
      >
        {/* Node Content */}
        <div ref={nodeContentRef} className="inline-flex items-center gap-3 node-content">
          {/* MetaRouter Cluster Logo with Environment Icon */}
          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 relative">
            <img src={mrClusterLogo} alt="MetaRouter Cluster" className="h-8 w-8" />
            
            {/* Environment Icon positioned relative to the MetaRouter icon */}
            {data.clusterEnvironment && data.clusterEnvironment.environmentId && (
              <div className="absolute bottom-[-5px] right-[-5px]">
                <img 
                  src={getEnvironmentIconPath(data.clusterEnvironment.environmentId)} 
                  alt={data.clusterEnvironment.name || "Environment"} 
                  className="h-4 w-4"
                />
              </div>
            )}
          </div>
          <div className="content-container flex-1 min-w-0">
            {/* Apply line-clamp-2 and leading-tight to allow wrapping up to two lines with reduced line height */}
            <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100 line-clamp-2">{data.label}</h3>
            {currentWidth >= NODE_DIMENSIONS.HIDE_LABELS_WIDTH_THRESHOLD && (
              <div className="labels-container flex flex-wrap gap-x-1">
                {/* Environment label - only show if visibility is not hidden */}
                {data.clusterEnvironment && data.environmentVisibility !== "hidden" && (
                  <span
                    className={`
                    mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate
                    ${getEnvironmentColor(data.clusterEnvironment.environmentId)}
                  `}
                  >
                    {data.clusterEnvironment.name}
                  </span>
                )}
                
                {/* Team Access label - only show if visibility is not hidden */}
                {data.teamAccess && data.teamAccessVisibility !== "hidden" && (
                  <span
                    className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium truncate bg-indigo-100 text-indigo-800"
                  >
                    <Users className="h-3 w-3 flex-shrink-0" />
                    {data.teamAccess === "direct"
                      ? "Direct Access"
                      : data.teamAccess === "other" && data.otherTeamAccess 
                        ? data.otherTeamAccess 
                        : data.teamAccess === "no-access" 
                          ? "No Access" 
                          : "Indirect Access"}
                  </span>
                )}
                
                {/* Custom Label - only show if visibility is not hidden */}
                {data.customLabel && data.customLabelVisibility !== "hidden" && (
                  <span
                    className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate bg-yellow-100 text-yellow-800"
                  >
                    {data.customLabel}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Enabled Modules - Show/hide based on moduleVisibility toggle and if there are enabled modules */}
        {currentHeight >= NODE_DIMENSIONS.HIDE_NOTES_HEIGHT_THRESHOLD && (() => {
          // Get enabled modules
          const enabledModules = Array.isArray(data.modules) ? data.modules.filter(
            (module: ModuleConfig) => module.enabled
          ) : [];
          
          // Only render the container if there are enabled modules and visibility is not hidden
          if (enabledModules.length > 0 && data.moduleVisibility !== "hidden") {
            return (
              <div className="flex flex-wrap gap-x-1 modules-container pt-1">
                {enabledModules.map((module: ModuleConfig) => {
                  const moduleInfo = MODULE_ICONS[module.moduleId];
                  if (!moduleInfo) return null;
                  
                  const Icon = moduleInfo.icon;
                  return (
                    <div
                      key={module.moduleId}
                      className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-600 rounded text-xs text-gray-600 dark:text-gray-100"
                    >
                      <Icon className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{moduleInfo.label}</span>
                    </div>
                  );
                })}
              </div>
            );
          }
          
          return null;
        })()}

        {/* --- Handle Rendering --- */}
        {Object.entries(handlesByPosition).map(([_, positionHandles]) =>
          positionHandles.map((handle, index) => (
            <Handle
              key={`${handle.id}-${handle.color}`} // Use unique key
              id={handle.id}
              type={handle.type}
              position={handle.position as Position}
              className={`!w-3 !h-3 !border-2 !border-white dark:!border-gray-700`} // Style like DestinationNode
              style={{
                ...calculateHandleStyle(index, positionHandles.length, handle.position),
                backgroundColor: handle.color, // Use configured color directly
              }}
              // Add isValidConnection if needed for specific handle types
            />
          ))
        )}
        {/* --- End Handle Rendering --- */}
      </div>
    </div>
  );
};

ClusterNode.displayName = 'ClusterNode';

export default memo(ClusterNode); // Wrap with memo
